/**
 * ``useMediaQuery(query)`` — reactive ``window.matchMedia`` wrapper.
 *
 * Returns ``true`` when the media query matches; updates on change.
 * Used by AppShell to swap desktop sidebar for mobile drawer+tabbar
 * below the design's ~700px breakpoint.
 *
 * Tests stub ``window.matchMedia`` (see ``web/src/test/setup.ts``);
 * the ``renderWithProviders`` helper exposes a ``media`` option that
 * pre-configures the response.
 */

import { useEffect, useState } from "react";

export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState<boolean>(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
      return false;
    }
    return window.matchMedia(query).matches;
  });

  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
      return;
    }
    const mql = window.matchMedia(query);
    const handler = (): void => setMatches(mql.matches);
    handler();  // sync initial state in case the SSR-time value drifted
    mql.addEventListener("change", handler);
    return () => mql.removeEventListener("change", handler);
  }, [query]);

  return matches;
}
